import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { PosterProject } from "../poster-project";
import { Size } from "../poster-types";
import { size } from "lodash";
export type Orientation = "portrait" | "landscape";

export interface SizePreset {
  id: string;
  sku: string;
  sizeDescription: string;
  materialDescription: string;
  aspectRatio: number; // width / height;
  widthInMM?: number | undefined;
  heightInMM?: number | undefined;
  priceInt?: number;
}

export const useSizePresets = () => {
  const { client } = useAuthContext();

  const fetchSizePresets = async (): Promise<SizePreset[]> => {
    if (!client) {
      throw new Error("Client not initialized");
    }

    const { data, error } = await client
      .from("physical_products")
      .select("*")
      .order("order", { ascending: true });

    if (error) throw error;

    const sizePresets: SizePreset[] = data.map((preset) => ({
      id: preset.id,
      sku: preset.sku,
      sizeDescription: preset.size_description,
      materialDescription: preset.material_description,
      aspectRatio: Number(preset.aspect_ratio),
      widthInMM: Number(preset.width_mm),
      heightInMM: Number(preset.height_mm),
      priceInt: preset.price_int ? Number(preset.price_int) : undefined,
    }));

    return sizePresets;
  };

  const {
    data: sizePresets,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["size-presets"],
    queryFn: fetchSizePresets,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  const sizePresetByID = (id: string): SizePreset | undefined => {
    return sizePresets?.find((preset) => preset.id === id);
  };

  const sizePresetFromProject = (
    project: PosterProject
  ): SizePreset | undefined => {
    const id = project.sizeOption.presetID;
    if (!id) {
      return undefined;
    }
    return sizePresetByID(id);
  };

  const projectAspectRatio = (project: PosterProject): number | undefined => {
    const presetID = project.sizeOption.presetID;
    if (!presetID) return undefined;
    const preset = sizePresetByID(presetID);

    if (preset?.widthInMM && preset?.heightInMM) {
      const aspectRatio = preset.widthInMM / preset.heightInMM;
      if (project.sizeOption.options.orientation === "portrait") {
        return 1 / aspectRatio;
      }
      return aspectRatio;
    } else if (
      project.sizeOption.options.widthPX &&
      project.sizeOption.options.heightPX
    ) {
      // For custom pixel dimensions, orientation doesn't apply
      // The pixel dimensions ARE the final dimensions
      const aspectRatio =
        project.sizeOption.options.widthPX /
        project.sizeOption.options.heightPX;
      return aspectRatio;
    }
    return undefined;
  };

  const screenSizeFromProject = (project: PosterProject) => {
    const aspectRatio = projectAspectRatio(project);
    if (!aspectRatio) {
      return undefined;
    }
    const maxHeight = 700;
    const maxWidth = 900;
    if (aspectRatio > 1) {
      return { width: maxWidth, height: maxWidth / aspectRatio };
    } else {
      return { width: maxHeight * aspectRatio, height: maxHeight };
    }
  };

  const cssAspectRatio = (project: PosterProject) => {
    return projectAspectRatio(project);
  };

  const sizeInCMFromProject = (
    project: PosterProject
  ): { width: number; height: number } | undefined => {
    const presetID = project.sizeOption.presetID;
    if (!presetID) return undefined;
    const preset = sizePresetByID(presetID);
    if (!preset) return undefined;
    const posterWidthInCustomPX = project.sizeOption.options.widthPX;
    const posterHeightInCustomPX = project.sizeOption.options.heightPX;
    let posterWidthInMM = preset.widthInMM;
    let posterHeightInMM = preset.heightInMM;

    if (posterWidthInCustomPX && posterHeightInCustomPX) {
      // We need to make an assumption that, say 100px is one cm.
      posterWidthInMM = posterWidthInCustomPX / 8;
      posterHeightInMM = posterHeightInCustomPX / 8;
    } else if (posterWidthInMM && posterHeightInMM) {
      // It's not a custom preset, so take into account orientation.
      // if it's portrait, swap width and height
      if (project.sizeOption.options.orientation === "portrait") {
        const temp = posterWidthInMM;
        posterWidthInMM = posterHeightInMM;
        posterHeightInMM = temp;
      }
    }
    if (!posterWidthInMM || !posterHeightInMM) return undefined;

    return { width: posterWidthInMM / 10, height: posterHeightInMM / 10 };
  };

  const borderWidthInPixels = (project: PosterProject) => {
    const sizeInCM = sizeInCMFromProject(project);
    if (!sizeInCM) return undefined;
    const posterWidthInMM = sizeInCM.width * 10;
    const fromMM = project.borderStyle.borderWidth ?? 0;
    const percentage = Math.abs(fromMM / posterWidthInMM);
    const posterPixelSize = screenSizeFromProject(project)?.width;
    if (!posterPixelSize) return undefined;

    const borderPixelWidth = posterPixelSize * percentage;
    return borderPixelWidth;
  };

  const borderRatioToPosterSize = (
    borderWidthInMM: number,
    project: PosterProject
  ): { horizontal: number; vertical: number } => {
    const width = sizeInCMFromProject(project)?.width ?? 0;
    const height = sizeInCMFromProject(project)?.height ?? 0;

    const posterWidthInMM = width * 10;
    const posterHeightInMM = height * 10;

    const ratioWidth = Math.abs(borderWidthInMM / posterWidthInMM);
    const ratioHeight = Math.abs(borderWidthInMM / posterHeightInMM);

    return { horizontal: ratioWidth, vertical: ratioHeight };
  };

  const incrementSizeInMM = 2.5;

  const incrementSizeInPX = (project: PosterProject) => {
    const screenSize = screenSizeFromProject(project);
    if (!screenSize) return undefined;

    const sizeInCM = sizeInCMFromProject(project);
    if (!sizeInCM) return undefined;
    const divisions = sizeInCM.width / 0.5; // 0.5 cm increments
    const divisionLength = screenSize.width / divisions;
    return divisionLength;
  };

  const nearestIncrementValueToValue = (
    value: number,
    incrementSizeInPX: number
  ) => {
    return Math.round(value / incrementSizeInPX) * incrementSizeInPX;
  };

  const fullRenderSizeFromProject = (
    project: PosterProject,
    sizeBoost: boolean
  ): { width: number; height: number } => {
    if (project.sizeOption === undefined) {
      return { width: 0, height: 0 };
    }
    const customWidth = project.sizeOption.options.widthPX;
    const customHeight = project.sizeOption.options.heightPX;

    if (
      customWidth !== undefined &&
      customWidth > 0 &&
      customHeight !== undefined &&
      customHeight > 0
    ) {
      return {
        width: customWidth,
        height: customHeight,
      };
    }

    const fullRender = process.env.REACT_APP_FULL_RENDER === "true";
    const maxDimension = maxRenderDimension(sizeBoost);
    const largest = (maxDimension / 2) * (fullRender ? 2 : 1);
    const aspectRatio = projectAspectRatio(project) ?? 1;
    let size: Size;
    if (aspectRatio > 1) {
      size = { width: largest, height: largest / aspectRatio };
    } else {
      size = { width: largest * aspectRatio, height: largest };
    }
    return {
      width: Math.floor(size.width),
      height: Math.floor(size.height),
    };
  };

  const mapRenderSizeFromProject = (
    project: PosterProject,
    sizeBoost: boolean
  ): { width: number; height: number } => {
    let size = fullRenderSizeFromProject(project, sizeBoost);
    const borderWidthInMM = project.borderStyle.borderWidth ?? 0;
    const borderRatios = borderRatioToPosterSize(borderWidthInMM, project);
    const borderThickness = size.height * borderRatios.vertical;
    const width = Math.round(size.width - borderThickness);
    const height = Math.round(size.height - borderThickness);

    return {
      width: Math.floor(width),
      height: Math.floor(height),
    };
  };

  const fullThumbnailSizeFromProject = (
    project: PosterProject
  ): { width: number; height: number } => {
    const renderSize = fullRenderSizeFromProject(project, false);
    const scale = 0.3;
    return {
      width: Math.floor(renderSize.width * scale),
      height: Math.floor(renderSize.height * scale),
    };
  };

  const mapThumbnailSizeFromProject = (
    project: PosterProject
  ): { width: number; height: number } => {
    const renderSize = mapRenderSizeFromProject(project, false);
    const scale = 0.3;
    return {
      width: Math.floor(renderSize.width * scale),
      height: Math.floor(renderSize.height * scale),
    };
  };

  const maxRenderDimension = (sizeBoost: boolean) => {
    const sizeBoostEnabled = process.env.REACT_APP_SIZE_BOOST === "true";
    return sizeBoostEnabled && sizeBoost ? 4000 : 2560;
  };

  return {
    sizePresets: sizePresets ?? [],
    isLoading,
    isFetching,
    isError,
    refetch,
    sizePresetByID,
    sizePresetFromProject,
    screenSizeFromProject,
    sizeInCMFromProject,
    cssAspectRatio,
    borderWidthInPixels,
    incrementSizeInPX,
    incrementSizeInMM,
    nearestIncrementValueToValue,
    mapRenderSizeFromProject,
    fullRenderSizeFromProject,
    maxRenderDimension,
    fullThumbnailSizeFromProject,
    mapThumbnailSizeFromProject,
  };
};
